import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail } from "lucide-react";
import Seo from "../components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";

const SECTIONS = [
    {
        title: "1. Adatkezelő",
        body:
            "Adatkezelő: Stabilor Kft. (székhely: Budapest). Elérhetőség: iroda@stabilor.hu · +36 70 653 4187. A Stabilor Kft. elkötelezett a Megrendelők személyes adatainak védelme mellett, és a GDPR-nak (EU 2016/679 rendelet), valamint a magyar Infotv. (2011. évi CXII. tv.) előírásainak megfelelően jár el.",
    },
    {
        title: "2. Kezelt adatok köre",
        body:
            "Az árajánlatkérés és szerződéskötés során a következő adatokat kezeljük: név, e-mail cím, telefonszám, helyszín / cím, valamint az igényelt szolgáltatáshoz kapcsolódó technikai adatok (pl. ingatlan típusa, áramfogyasztás). Kifejezetten nem kezelünk különleges adatkategóriába tartozó adatokat.",
    },
    {
        title: "3. Adatkezelés célja",
        body:
            "Az adatok kezelésének célja: kapcsolatfelvétel, árajánlatadás, szerződés előkészítése és teljesítése, garanciális ügyintézés, valamint a jogszabályi kötelezettségek teljesítése (számvitel, garanciális kötelezettségek).",
    },
    {
        title: "4. Jogalap",
        body:
            "Az adatkezelés jogalapja az Ön hozzájárulása (GDPR 6. cikk (1) a)), a szerződés teljesítése (6. cikk (1) b)), illetve jogszabályi kötelezettség teljesítése (6. cikk (1) c)) — pl. számlázás esetén.",
    },
    {
        title: "5. Adatok megőrzési ideje",
        body:
            "A kapcsolatfelvételi adatokat legfeljebb 1 évig őrizzük meg, ha nem jön létre szerződés. Szerződéses kapcsolat esetén az adatokat a szerződés megszűnését követő 8 évig (számviteli kötelezettség), illetve a garanciális idő végéig őrizzük.",
    },
    {
        title: "6. Adatok továbbítása",
        body:
            "A személyes adatokat harmadik fél részére nem adjuk át, kivéve: (a) jogszabályi kötelezettség alapján hatóság részére, (b) alvállalkozók részére kizárólag a szerződés teljesítéséhez szükséges mértékben, megfelelő titoktartási kötelezettség mellett.",
    },
    {
        title: "7. Az Ön jogai",
        body:
            "Önnek joga van: (a) tájékoztatást kérni az adatkezelésről, (b) adatainak helyesbítését kérni, (c) adatainak törlését kérni (elfeledtetéshez való jog), (d) az adatkezelés korlátozását kérni, (e) adathordozhatóságot kérni, (f) tiltakozni az adatkezelés ellen, (g) hozzájárulását bármikor visszavonni. Jogainak gyakorlásához írjon a schell.viktor@gmail.com címre.",
    },
    {
        title: "8. Cookie-k",
        body:
            "A weboldal a működéshez szükséges, technikai cookie-kat használhat (pl. nyelvi beállítás). Marketing célú nyomonkövető cookie-kat csak az Ön kifejezett hozzájárulásával alkalmazunk.",
    },
    {
        title: "9. Panaszjog",
        body:
            "Amennyiben úgy ítéli meg, hogy az adatkezelés a vonatkozó jogszabályokat sérti, panaszt nyújthat be a Nemzeti Adatvédelmi és Információszabadság Hatósághoz (NAIH, 1055 Budapest, Falk Miksa utca 9-11.), valamint bírósághoz fordulhat.",
    },
];

export default function AdatvedelemPage() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div
            className="bg-white min-h-screen flex flex-col"
            data-testid="adatvedelem-page"
        >
            <Seo
                title="Adatvédelmi Nyilatkozat"
                description="A Stabilor adatkezelési tájékoztatója. Személyes adatok kezelése a GDPR és a magyar adatvédelmi jogszabályok szerint."
                path="/adatvedelem"
                breadcrumbs={[
                    { name: "Főoldal", path: "/" },
                    { name: "Adatvédelem", path: "/adatvedelem" },
                ]}
            />
            <Navbar />
            <main className="flex-1">
                <section className="bg-[#F8FAFC] border-b border-black/10 pt-32 sm:pt-40 pb-16 sm:pb-24">
                    <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
                        <Link
                            to="/"
                            data-testid="legal-back-link"
                            className="inline-flex items-center gap-2 font-mono-label text-xs text-slate-500 hover:text-[#0D7A73] transition-colors mb-8"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Vissza a főoldalra
                        </Link>
                        <div className="section-num">Adatkezelési tájékoztató</div>
                        <h1
                            className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                            data-testid="legal-title"
                        >
                            Adatvédelmi Nyilatkozat
                        </h1>
                        <p className="mt-6 text-slate-700 text-lg leading-relaxed max-w-2xl">
                            A Stabilor a Megrendelőinek személyes adatait a GDPR és a
                            magyar adatvédelmi jogszabályok szerint kezeli. Az
                            alábbiakban részletesen tájékoztatjuk az adatkezelésünk
                            gyakorlatáról.
                        </p>
                        <div className="mt-8 font-mono-label text-xs text-slate-500">
                            Hatályos: {new Date().getFullYear()}. január 1.
                        </div>
                    </div>
                </section>

                <section className="py-16 sm:py-24">
                    <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 space-y-12">
                        {SECTIONS.map((s, i) => (
                            <div
                                key={s.title}
                                data-testid={`adatvedelem-section-${i + 1}`}
                                className="border-l-2 border-[#0D7A73] pl-6 sm:pl-8"
                            >
                                <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight">
                                    {s.title}
                                </h2>
                                <p className="mt-4 text-slate-700 text-base sm:text-lg leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="bg-[#0A0A0A] text-white py-16 sm:py-20">
                    <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <div>
                            <div className="font-mono-label text-xs text-white/60">
                                Kérdése van?
                            </div>
                            <h3 className="mt-2 font-display font-bold text-2xl sm:text-3xl">
                                Forduljon hozzánk bizalommal.
                            </h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="tel:+36706534187"
                                data-testid="adatvedelem-cta-phone"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0D7A73] hover:bg-[#0A5E58] font-bold transition-colors"
                            >
                                <Phone className="w-4 h-4" /> +36 70 653 4187
                            </a>
                            <a
                                href="mailto:iroda@stabilor.hu"
                                data-testid="adatvedelem-cta-email"
                                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white/60 hover:border-white font-bold transition-colors"
                            >
                                <Mail className="w-4 h-4" /> Email
                            </a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
